<template>
  <div class="space-y-5">

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div class="bg-gray-900 rounded-xl p-4 border border-blue-800/40">
        <p class="text-xs text-blue-400 mb-1">Total Análises</p>
        <p class="text-2xl font-bold text-white">{{ analyses.length }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-purple-800/40">
        <p class="text-xs text-purple-400 mb-1">Confiança Média</p>
        <p class="text-2xl font-bold text-white">{{ averageConfidence.toFixed(1) }}%</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-green-800/40">
        <p class="text-xs text-green-400 mb-1">Recomendações BUY</p>
        <p class="text-2xl font-bold text-green-400">{{ buyCount }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs text-gray-500 mb-1">Analisados hoje</p>
        <p class="text-2xl font-bold text-white">{{ todayCount }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-amber-800/40">
        <p class="text-xs text-amber-400 mb-1">Edge médio do modelo</p>
        <p class="text-2xl font-bold text-amber-300">{{ researchSummary.averageEdge.toFixed(2) }}%</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs text-gray-500 mb-1">Simulações pendentes</p>
        <p class="text-2xl font-bold text-white">{{ researchSummary.pending }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-green-800/40">
        <p class="text-xs text-green-400 mb-1">P&L paper trading</p>
        <p class="text-2xl font-bold" :class="researchSummary.paperPnl >= 0 ? 'text-green-300' : 'text-red-300'">{{ researchSummary.paperPnl.toFixed(2) }}</p>
        <p class="text-[11px] text-gray-600 mt-1">{{ researchSummary.paperWinRate.toFixed(1) }}% win rate · PF {{ researchSummary.paperProfitFactor }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-red-800/40">
        <p class="text-xs text-red-400 mb-1">Drawdown máximo</p>
        <p class="text-2xl font-bold text-red-300">{{ researchSummary.maxDrawdown.toFixed(2) }}</p>
        <p class="text-[11px] text-gray-600 mt-1">Somente trades resolvidos</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-orange-800/40">
        <p class="text-xs text-orange-400 mb-1">Pesquisa esportiva IABit</p>
        <p class="text-2xl font-bold text-orange-300">{{ researchSummary.sportsSnapshots }}</p>
        <p class="text-[11px] text-gray-600 mt-1">{{ researchSummary.sportsFixtures }} partidas · {{ researchSummary.sportsFinished }} finalizadas</p>
      </div>
    </div>

    <div v-if="researchSummary.venues.length || researchSummary.strategies.length" class="grid md:grid-cols-2 gap-3">
      <div v-if="researchSummary.venues.length" class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs uppercase tracking-wider text-gray-500 mb-3">Comparação por venue</p>
        <div v-for="venue in researchSummary.venues" :key="venue.venue" class="flex items-center justify-between py-1.5 border-b border-gray-800 last:border-0">
          <span class="text-xs text-gray-300">{{ venue.venue }}</span>
          <span class="text-xs font-bold" :class="venue.average_edge_percent > 0 ? 'text-green-400' : 'text-gray-400'">{{ Number(venue.average_edge_percent || 0).toFixed(2) }}% · {{ venue.analyses }}</span>
        </div>
      </div>
      <div v-if="researchSummary.strategies.length" class="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <p class="text-xs uppercase tracking-wider text-gray-500 mb-3">Comparação por estratégia</p>
        <div v-for="strategy in researchSummary.strategies" :key="strategy.strategy" class="flex items-center justify-between py-1.5 border-b border-gray-800 last:border-0">
          <span class="text-xs text-gray-300">{{ strategy.strategy }}</span>
          <span class="text-xs font-bold" :class="strategy.average_edge_percent > 0 ? 'text-green-400' : 'text-gray-400'">{{ Number(strategy.average_edge_percent || 0).toFixed(2) }}% · {{ strategy.analyses }}</span>
        </div>
      </div>
    </div>

    <div v-if="researchSummary.realizedVenues.length" class="bg-gray-900 rounded-xl p-4 border border-gray-800">
      <p class="text-xs uppercase tracking-wider text-gray-500 mb-3">Resultado realizado por venue</p>
      <div class="grid md:grid-cols-3 gap-2">
        <div v-for="venue in researchSummary.realizedVenues" :key="venue.venue" class="bg-gray-800/50 rounded-lg px-3 py-2">
          <p class="text-xs text-gray-300 truncate">{{ venue.venue }}</p>
          <p class="text-sm font-bold" :class="Number(venue.pnl) >= 0 ? 'text-green-400' : 'text-red-400'">{{ Number(venue.pnl || 0).toFixed(2) }}</p>
          <p class="text-[10px] text-gray-500">{{ venue.trades }} trades · PF {{ venue.profit_factor == null ? '-' : Number(venue.profit_factor).toFixed(2) }}</p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-gray-900 rounded-2xl p-4 border border-gray-800 flex flex-wrap gap-3 items-end">
      <!-- Tier filter -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Nível</label>
        <div class="flex gap-1.5">
          <button v-for="t in tierOptions" :key="t.value"
            @click="filterTier = filterTier === t.value ? '' : t.value"
            class="px-3 py-1.5 rounded-lg text-xs font-bold border transition-all"
            :class="filterTier === t.value ? t.activeClass : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'"
          >{{ t.label }}</button>
        </div>
      </div>
      <!-- Recommendation filter -->
      <div>
        <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Recomendação</label>
        <div class="flex gap-1.5">
          <button v-for="r in recOptions" :key="r.value"
            @click="filterRec = filterRec === r.value ? '' : r.value"
            class="px-3 py-1.5 rounded-lg text-xs font-bold border transition-all"
            :class="filterRec === r.value ? r.activeClass : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'"
          >{{ r.label }}</button>
        </div>
      </div>
      <!-- Search -->
      <div class="flex-1 min-w-[180px]">
        <label class="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">Buscar</label>
        <input v-model="searchQuery" type="text" placeholder="Questão do mercado..."
          class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-500/50" />
      </div>
      <button @click="clearFilters" class="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-xs font-medium text-gray-400 transition-all">Limpar</button>
    </div>

    <!-- Results count -->
    <p class="text-xs text-gray-500">{{ totalFiltered }} análises encontradas · página {{ currentPage }}/{{ totalPages }}</p>

    <!-- Analyses List -->
    <div class="space-y-2">
      <div v-if="pagedAnalyses.length === 0" class="bg-gray-900 rounded-2xl p-12 border border-gray-800 text-center">
        <span class="text-4xl block mb-3">🤖</span>
        <p class="text-gray-500 text-sm">Nenhuma análise encontrada</p>
      </div>

      <div v-for="analysis in pagedAnalyses" :key="analysis.id"
        class="bg-gray-900 rounded-xl border border-gray-800 hover:border-gray-700 transition-all duration-200 overflow-hidden"
        :class="{ 'border-l-2 border-l-green-500': analysis.recommendation === 'BUY', 'border-l-2 border-l-red-500': analysis.recommendation === 'SELL' }"
      >
        <div class="p-4">
          <!-- Header row -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-white leading-snug truncate">
                {{ analysis.marketQuestion || analysis.question || analysis.marketId || analysis.market_id || 'Mercado sem descrição' }}
              </p>
              <p class="text-xs text-gray-600 mt-0.5">{{ formatDate(analysis.timestamp) }}</p>
            </div>
            <!-- Badges -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <!-- Tier -->
              <span class="px-2 py-0.5 rounded text-xs font-bold" :class="tierBadgeClass(analysis.confidence)">
                {{ getTier(analysis.confidence) }}
              </span>
              <!-- Recommendation -->
              <span class="px-2.5 py-1 rounded-lg text-xs font-bold border"
                :class="{
                  'bg-green-500/20 text-green-400 border-green-500/20': analysis.recommendation === 'BUY',
                  'bg-red-500/20 text-red-400 border-red-500/20': analysis.recommendation === 'SELL',
                  'bg-gray-700 text-gray-300 border-gray-600': analysis.recommendation === 'HOLD',
                }"
              >{{ analysis.recommendation }}</span>
              <!-- Confidence bar -->
              <div class="flex items-center gap-1.5">
                <div class="w-16 bg-gray-800 rounded-full h-1.5">
                  <div class="h-1.5 rounded-full transition-all duration-500"
                    :class="{ 'bg-teal-500': analysis.confidence >= 70, 'bg-blue-500': analysis.confidence >= 60 && analysis.confidence < 70, 'bg-violet-500': analysis.confidence < 60 }"
                    :style="{ width: analysis.confidence + '%' }"
                  ></div>
                </div>
                <span class="text-xs font-bold w-8 text-right"
                  :class="{ 'text-teal-400': analysis.confidence >= 70, 'text-blue-400': analysis.confidence >= 60 && analysis.confidence < 70, 'text-violet-400': analysis.confidence < 60 }"
                >{{ analysis.confidence }}%</span>
              </div>
            </div>
          </div>

          <!-- AI Reasoning (collapsible) -->
          <div v-if="analysis.reasoning" class="mt-2.5">
            <button @click="toggleReasoning(analysis.id)" class="text-xs text-gray-500 hover:text-gray-300 transition flex items-center gap-1">
              <span>{{ expandedIds.has(analysis.id) ? '▾' : '▸' }}</span>
              Raciocínio IA
            </button>
            <div v-if="expandedIds.has(analysis.id)" class="mt-2 bg-gray-800/50 rounded-lg p-3 border border-gray-700/40">
              <p class="text-xs text-gray-300 leading-relaxed">{{ analysis.reasoning }}</p>
            </div>
          </div>

          <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
            <span v-if="analysis.venue">Venue: <strong class="text-gray-300">{{ analysis.venue }}</strong></span>
            <span v-if="analysis.strategy">Estratégia: <strong class="text-gray-300">{{ analysis.strategy }}</strong></span>
            <span v-if="analysis.edge_percent !== undefined">Edge modelo: <strong :class="analysis.edge_percent > 0 ? 'text-green-400' : 'text-gray-400'">{{ Number(analysis.edge_percent || 0).toFixed(2) }}%</strong></span>
            <span v-if="analysis.liquidity">Liquidez: <strong class="text-gray-300">{{ Number(analysis.liquidity).toLocaleString('en-US', { maximumFractionDigits: 0 }) }}</strong></span>
            <span v-if="analysis.simulation_status">Simulação: <strong class="text-yellow-400">{{ analysis.simulation_status }}</strong></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="flex justify-between items-center">
      <p class="text-xs text-gray-500">{{ totalFiltered > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}–{{ Math.min(currentPage * pageSize, totalFiltered) }} de {{ totalFiltered }}</p>
      <div class="flex items-center gap-1.5">
        <button @click="currentPage = 1" :disabled="currentPage === 1" class="px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">««</button>
        <button @click="currentPage--" :disabled="currentPage === 1" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">‹</button>
        <span class="px-3 py-1.5 text-xs text-gray-400 bg-gray-800/50 rounded-lg">{{ currentPage }} / {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage >= totalPages" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">›</button>
        <button @click="currentPage = totalPages" :disabled="currentPage >= totalPages" class="px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">»»</button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { getApiBaseUrl } from '../config/runtime';
import { useAuthStore } from '../stores/authStore';

const authStore = useAuthStore();

const analyses = ref([]);
const researchSummary = ref({ averageEdge: 0, pending: 0, paperPnl: 0, paperWinRate: 0, paperProfitFactor: '-', maxDrawdown: 0, sportsSnapshots: 0, sportsFixtures: 0, sportsFinished: 0, venues: [], strategies: [], realizedVenues: [] });
const filterTier = ref('');
const filterRec = ref('');
const searchQuery = ref('');
const currentPage = ref(1);
const pageSize = 10;
const expandedIds = ref(new Set());
let refreshInterval = null;

const tierOptions = [
  { value: 'CONSERVADOR', label: 'Conservador', activeClass: 'bg-teal-500/20 text-teal-400 border-teal-500/40' },
  { value: 'MODERADO',    label: 'Moderado',    activeClass: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  { value: 'EXTREMO',     label: 'Extremo',     activeClass: 'bg-violet-500/20 text-violet-400 border-violet-500/40' },
];

const recOptions = [
  { value: 'BUY',  label: 'BUY',  activeClass: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { value: 'SELL', label: 'SELL', activeClass: 'bg-red-500/20 text-red-400 border-red-500/40' },
  { value: 'HOLD', label: 'HOLD', activeClass: 'bg-gray-600 text-gray-200 border-gray-500' },
];

function getTier(conf) {
  if ((conf || 0) >= 70) return 'CONSERVADOR';
  if ((conf || 0) >= 60) return 'MODERADO';
  return 'EXTREMO';
}

function tierBadgeClass(conf) {
  const t = getTier(conf);
  if (t === 'CONSERVADOR') return 'bg-teal-500/20 text-teal-400';
  if (t === 'MODERADO')    return 'bg-blue-500/20 text-blue-400';
  return 'bg-violet-500/20 text-violet-400';
}

function toggleReasoning(id) {
  const s = new Set(expandedIds.value);
  s.has(id) ? s.delete(id) : s.add(id);
  expandedIds.value = s;
}

const averageConfidence = computed(() => {
  if (!analyses.value.length) return 0;
  return analyses.value.reduce((s, a) => s + (a.confidence || 0), 0) / analyses.value.length;
});

const buyCount = computed(() => analyses.value.filter(a => a.recommendation === 'BUY').length);

const todayCount = computed(() => {
  const today = new Date().toDateString();
  return analyses.value.filter(a => a.timestamp && new Date(a.timestamp).toDateString() === today).length;
});

const allFiltered = computed(() => analyses.value.filter(a => {
  if (filterTier.value && getTier(a.confidence) !== filterTier.value) return false;
  if (filterRec.value && a.recommendation !== filterRec.value) return false;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    if (!((a.marketQuestion || a.question || a.marketId || a.market_id || '').toLowerCase().includes(q))) return false;
  }
  return true;
}));

const totalFiltered = computed(() => allFiltered.value.length);
const totalPages    = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize)));
const pagedAnalyses = computed(() => allFiltered.value.slice((currentPage.value - 1) * pageSize, currentPage.value * pageSize));

watch([filterTier, filterRec, searchQuery], () => { currentPage.value = 1; });

const formatDate = (d) => d ? new Date(d).toLocaleString('pt-BR') : '-';
function clearFilters() { filterTier.value = ''; filterRec.value = ''; searchQuery.value = ''; }

async function fetchAnalyses() {
  try {
    const headers = authStore.token
      ? { Authorization: `Bearer ${authStore.token}` }
      : { 'X-API-Key': '' };
    const endpoint = authStore.token ? '/analyses?limit=2000' : '/public/research/analyses?limit=2000';
    const res = await fetch(`${getApiBaseUrl()}${endpoint}`, { headers });
    if (res.ok) {
      const data = await res.json();
      analyses.value = (data.analyses || []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  } catch (e) {
    console.error('Erro ao buscar análises:', e);
  }
}

async function fetchResearchSummary() {
  try {
    const headers = authStore.token
      ? { Authorization: `Bearer ${authStore.token}` }
      : { 'X-API-Key': '' };
    const endpoint = authStore.token ? '/research/summary' : '/public/research/summary';
    const res = await fetch(`${getApiBaseUrl()}${endpoint}`, { headers });
    if (!res.ok) return;
    const data = await res.json();
    researchSummary.value = {
      averageEdge: Number(data.totals?.average_edge_percent || 0),
      pending: Number(data.totals?.pending_simulations || 0),
      paperPnl: Number(data.paperTrading?.pnl || 0),
      paperWinRate: Number(data.paperTrading?.winRate || 0),
      paperProfitFactor: data.paperTrading?.profitFactor == null ? '-' : Number(data.paperTrading.profitFactor).toFixed(2),
      maxDrawdown: Number(data.paperTrading?.maxDrawdown || 0),
      sportsSnapshots: Number(data.sports?.snapshots || 0),
      sportsFixtures: Number(data.sports?.fixtures || 0),
      sportsFinished: Number(data.sports?.finished || 0),
      venues: data.byVenue || [],
      strategies: data.byStrategy || [],
      realizedVenues: data.paperTrading?.byVenue || [],
    };
  } catch (e) {
    console.error('Erro ao buscar resumo da pesquisa:', e);
  }
}

onMounted(() => {
  fetchAnalyses();
  fetchResearchSummary();
  refreshInterval = setInterval(fetchAnalyses, 30000);
});

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval);
});
</script>
